/**
 * Switches a Before / After section between treated areas.
 *
 * Each area is a tab that can carry its own pair of photographs. There is one
 * comparison slider for the whole section, and choosing an area swaps the
 * pictures inside it rather than the slider itself — so the slider keeps its
 * place on the page and the drag position the shopper left it at.
 *
 * An area without photographs shows the section's own pair. Without JavaScript
 * the section's photographs stay on screen and the slider still works.
 */
class BeforeAfterAreas extends HTMLElement {
  #request = 0;

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-ba-tab]'));
    this.photos = {
      before: this.querySelector('.comparison-slider img.before-image'),
      after: this.querySelector('.comparison-slider img.after-image'),
    };

    // The section's own pair, restored for any area that has none.
    this.fallback = {
      before: this.photos.before?.cloneNode(),
      after: this.photos.after?.cloneNode(),
    };

    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onClick);
      tab.addEventListener('keydown', this.onKeydown);
      tab.addEventListener('pointerenter', this.onIntent);
      tab.addEventListener('focus', this.onIntent);
    });

    this.select(0);
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.onClick);
      tab.removeEventListener('keydown', this.onKeydown);
      tab.removeEventListener('pointerenter', this.onIntent);
      tab.removeEventListener('focus', this.onIntent);
    });
  }

  onClick = (event) => {
    this.select(this.tabs.indexOf(event.currentTarget));
  };

  onKeydown = (event) => {
    const current = this.tabs.indexOf(event.currentTarget);
    let next = null;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = current + 1;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = current - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    const index = (next + this.tabs.length) % this.tabs.length;
    this.select(index);
    this.tabs[index].focus();
  };

  // Start fetching an area's photographs as soon as the shopper reaches for its tab.
  onIntent = (event) => {
    const { before, after } = this.#photosFor(this.tabs.indexOf(event.currentTarget));
    this.#load(before, this.photos.before);
    this.#load(after, this.photos.after);
  };

  select(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.#showPhotos(index);
  }

  #photosFor(index) {
    const own = this.tabs[index]?.querySelector('template[data-ba-images]')?.content;

    return {
      before: own?.querySelector('.before-image') ?? this.fallback.before,
      after: own?.querySelector('.after-image') ?? this.fallback.after,
    };
  }

  async #showPhotos(index) {
    const request = ++this.#request;
    const { before, after } = this.#photosFor(index);

    // Swap only once both pictures have loaded, so the slider never shows one
    // half of the new pair against the old, or flashes empty while loading.
    const [nextBefore, nextAfter] = await Promise.all([
      this.#load(before, this.photos.before),
      this.#load(after, this.photos.after),
    ]);

    // A later click has already taken over.
    if (request !== this.#request) return;

    this.#adopt(this.photos.before, nextBefore);
    this.#adopt(this.photos.after, nextAfter);
  }

  /**
   * Fetches the source picture off the page at the width the slider shows it,
   * so the swap paints from cache and the browser picks the same file it would
   * for the image in place. Resolves with the source once it is ready.
   *
   * The probe is a fresh image rather than a copy: the slider's own pictures are
   * lazy, and a lazy image that is not on the page never starts loading. It waits
   * for the file rather than for decode(), which only settles once the page
   * paints — never, in a background tab.
   */
  async #load(source, target) {
    if (!source || !target || source.getAttribute('srcset') === target.getAttribute('srcset')) return null;

    // The slider, not the picture: the before layer is clipped to the handle.
    const width = target.closest('.comparison-slider')?.getBoundingClientRect().width;

    const probe = new Image();
    probe.sizes = `${Math.round(width || window.innerWidth)}px`;
    probe.srcset = source.getAttribute('srcset') ?? '';
    probe.src = source.getAttribute('src') ?? '';

    // Swap regardless after a few seconds: the picture still arrives, it only
    // paints a moment later, which beats a slider stuck on the wrong area.
    await new Promise((resolve) => {
      if (probe.complete) return resolve();
      probe.addEventListener('load', resolve, { once: true });
      probe.addEventListener('error', resolve, { once: true });
      setTimeout(resolve, 4000);
    });

    return source;
  }

  // Take on the new picture's source while keeping the element the slider holds.
  #adopt(target, source) {
    if (!target || !source) return;

    for (const name of ['src', 'srcset', 'alt', 'width', 'height']) {
      const value = source.getAttribute(name);
      if (value === null) target.removeAttribute(name);
      else target.setAttribute(name, value);
    }
  }
}

if (!customElements.get('before-after-areas')) {
  customElements.define('before-after-areas', BeforeAfterAreas);
}
